<template>
  <div class="main-container-fluid vision text-medium">
    <div class="col-12 col-md-10 col-lg-10 m-auto">
      <div v-if="!isVisionAllowed">
        <vision-paywall />
      </div>
      <div class="loader" v-else-if="isFetching">
        <div class="shine mb-1"></div>
        <div class="shine mb-1"></div>
        <div class="shine"></div>
      </div>
      <vision-permission-denied v-else-if="isGuestUser" />
      <div v-else-if="viewType === VIEWS.READ">
        <with-gallery v-if="vision" :update-watcher="vision">
          <div class="read-view">
            <div v-html="vision"></div>
            <span class="edit-icon" @click="viewType = VIEWS.EDIT" v-if="isOwner"><i></i></span>
          </div>
        </with-gallery>
        <div class="read-view" v-else>
          <div v-if="isOwner">
            <div>
              Vision and strategy allows everyone to see how your goals align to the big picture.
            </div>
            <div class="mt-1 vision-action">
              <b-button
                variant="link"
                class="text-secondary pl-0 text-medium add-vision"
                @click="viewType = VIEWS.EDIT"
                >Add a vision
              </b-button>
              <b-button
                variant="link"
                class="text-secondary text-medium"
                @click="visitVisionTemplates"
                >Vision and Strategy Templates
              </b-button>
            </div>
          </div>
          <div v-else>
            Looks like there is no vision statement set at the moment, but when it is, you'll see it
            here. Only team or org owners can set the Vision statement
          </div>
        </div>
      </div>
      <template v-else-if="viewType === VIEWS.EDIT && mentionableUsers.length > 0">
        <ck-editor-form
          class="cke-editor-form"
          :initial-data="vision"
          :org-id="orgId"
          :error="error"
          :mentionable-users="mentionableUsers"
          @save="onSubmit"
          @cancel="cancelEdit"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { trackEvent } from '@/analytics'
import CkEditorForm from '@/components/CkEDitorForm/CkEditorForm.vue'
import VisionPaywall from '@/components/paywalls/VisionPaywall'
import VisionPermissionDenied from '@/components/vision/VisionPermissionDenied'
import WithGallery from '@/components/WithGallery'
import { EVENT_ACTION, EVENT_CATEGORY } from '@/constants/analytics'
import { VIEW_TYPE } from '@/constants/common'
import { featureFlags } from '@/constants/featureFlags'
import { USER_TYPE } from '@/constants/user'
import EventBus from '@/EventBus'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import pickError from '@/utils/pickError'
import * as R from 'ramda'
import { BButton } from 'bootstrap-vue'
import { mapActions } from 'vuex'
import showAPIError from '@/utils/showAPIError'
import updateEditorHTML from '@/utils/updateEditorHTML'
import { getCKEditorConfig } from '@/ckeditorConfig'

/**
 * This component provides displaying/editing of org vision.
 * props:
 * - orgId
 * - teamId: In case of parent-most org, it is rootTeamId
 */
export default {
  name: 'Vision',
  props: {
    orgId: [String, Number],
    teamId: [String, Number]
  },
  mixins: [FeaturesMixin, MembershipsMixin],
  data() {
    return {
      viewType: VIEW_TYPE.READ,
      VIEWS: VIEW_TYPE,
      isFetching: false,
      isGuestUser: false,
      error: null,
      isVisionAllowed: this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_VISION)
    }
  },
  components: {
    CkEditorForm,
    BButton,
    WithGallery,
    VisionPaywall,
    VisionPermissionDenied
  },
  computed: {
    userId() {
      return this.$store.getters.user.id
    },
    org() {
      return this.$store.getters.orgById(this.orgId)
    },
    vision() {
      const vision = this.$store.state.vision.vision
      let visionText = R.path(['teams', this.teamId], vision)
      if (visionText) {
        return updateEditorHTML(visionText, this.orgId)
      }
      return ''
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    teamMembers() {
      return this.$store.getters.teamMemberships(this.teamId)
    },
    mentionableUsers() {
      return this.teamMembers.map(x => x.user)
    },
    ckeditorConfig() {
      return getCKEditorConfig({
        height: 300,
        startupFocus: true,
        users: this.mentionableUsers,
        orgId: this.orgId
      })
    },
    isOwner() {
      const membershipType = this.$store.getters.computeMembershipTypeForTeamId(this.teamId)
      return membershipType === USER_TYPE.OWNER
    }
  },
  methods: {
    ...mapActions('vision', ['getTeamVision', 'updateTeamVision']),
    async onSubmit(vision) {
      this.error = null
      try {
        await this.updateTeamVision({ vision, orgId: this.orgId, teamId: this.teamId })
        this.viewType = VIEW_TYPE.READ
        EventBus.$emit('ADD_TOAST', { message: 'Changes saved successfully' })
      } catch (e) {
        this.error = pickError(e)
      }
    },
    async fetchVision() {
      try {
        await this.getTeamVision({ orgId: this.orgId, teamId: this.teamId })
      } catch (e) {
        if (R.path(['response', 'status'], e) !== 404) {
          showAPIError(e)
        }
      }
    },
    visitVisionTemplates() {
      trackEvent(EVENT_CATEGORY.HELP, EVENT_ACTION.VISION_TEMPLATE_SEEN)
      window.open('https://thenorth.io/org-vision-and-strategy/', '_blank')
    },
    evaluateGuestUser() {
      const membershipType = this.$store.getters.computeMembershipTypeForTeamId(this.teamId)
      this.isGuestUser = [USER_TYPE.GUEST, USER_TYPE.GUEST_NO_ACCESS].includes(membershipType)
    },
    cancelEdit() {
      this.viewType = VIEW_TYPE.READ
      this.error = null
    }
  },
  async mounted() {
    if (!this.isVisionAllowed) {
      return
    }
    this.isFetching = true
    try {
      await this.fetchTeamMemberships(this.orgId, this.teamId)
    } catch (e) {
      console.log('Could not fetch team memberships', this.teamId)
    }
    this.evaluateGuestUser()
    if (!this.isGuestUser) {
      try {
        await this.fetchVision()
      } catch (e) {
        console.log('Could not fetch team vision')
      }
    }
    this.isFetching = false
  },
  watch: {
    teamId: 'fetchVision'
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

$left-spacing: 64px;

.loader {
  padding-left: $left-spacing;
}

.read-view {
  padding-left: $left-spacing;
  padding-right: 20px;
  position: relative;
}

.edit-icon {
  position: absolute;
  right: 0;
  top: 0;
}

.cke-editor-form {
  margin-left: $left-spacing - 8px;
  padding: 10px 25px 25px 25px;
  box-shadow: 0 2px 5px rgba(62, 62, 62, 0.25);
  border-radius: 16px;
  min-height: 414px;
  border: 1px solid transparent;
}

.field-error-message {
  padding-left: $left-spacing;
}

.vision-action {
  display: flex;
}

.see-templates {
  &,
  &:hover {
    color: $secondary;
  }
}

.shine {
  height: 13px;
  width: 350px;
}

@include media-breakpoint-down(md) {
  .read-view {
    padding-left: 0;
  }
  .cke-editor-form {
    margin-left: 0px;
  }
  .loader {
    padding-left: 0;
  }
}

@include media-breakpoint-down(sm) {
  .edit-icon,
  .vision-action {
    display: none;
  }
}
</style>
